// Handler factory for workspace lifecycle — create, rename, delete, share,
// remove member, move transaction, and create-and-move transaction.

import * as workspaceService from '../services/workspaceService';
import * as transactionService from '../services/transactionService';
import * as invitationService from '../services/invitationService';
import { DEFAULT_WORKSPACE_DATA_ENTRY_SETTINGS, sanitizeWorkspaceDataEntrySettings } from '../config/workspaceDefaults';
import { isSoldStatus } from '../config/statuses';
import { reportRelocateFailure } from '../utils/movedDealFiles';

const MANAGE_ACCESS_OWNER_ONLY_MESSAGE = 'Only the team owner can manage access.';
const MOVED_FILES_PENDING_MESSAGE = 'Moved. Its files will reappear when you open the deal.';
const MOVED_EMAIL_PENDING_MESSAGE = 'Moved. Its email history will finish moving when you open the deal.';
const MOVED_BOTH_PENDING_MESSAGE = 'Moved. Its files and email history will finish moving when you open the deal.';

const hasTransactionFiles = (transaction) =>
    Array.isArray(transaction?.transactionFiles) && transaction.transactionFiles.length > 0;

export function useWorkspaceHandlers({
    user,
    selectedWorkspaceId,
    selectedWorkspace,
    canManageWorkspaceAccess,
    run,
    showToast,
    setSelectedWorkspaceId,
    setCreateWorkspaceModalOpen,
    setDeleteWorkspaceModalOpen,
    setMoveTransactionModalOpen,
    setWorkspaceMembers,
    transactionToMove,
    setTransactionToMove,
    // Optional: id -> workspace name, for plain-language move toasts.
    getWorkspaceName = () => undefined,
}) {
    // CR-01 and Phase 62.1: a move or assign writes the deal under a new id, so
    // its file entries still name the old deal (the quarantine trigger hides
    // them) and its TC address and email history still sit with the old deal.
    // Ask the server to relocate both. The move already succeeded, so a failure
    // here is non-fatal: the deal's own page retries on open. A resolved answer,
    // skipped or not, is a success.
    const relocateAfterMove = async (transaction, workspaceId, moved) => {
        const transactionId = moved?.id;
        if (!workspaceId || !transactionId) return;
        const [filesResult, emailResult] = await Promise.allSettled([
            hasTransactionFiles(transaction)
                ? transactionService.relocateMovedTransactionFiles(workspaceId, transactionId)
                : Promise.resolve(null),
            transactionService.relocateMovedDealEmail(workspaceId, transactionId),
        ]);
        const filesFailed = filesResult.status === 'rejected';
        const emailFailed = emailResult.status === 'rejected';
        if (filesFailed) {
            reportRelocateFailure(filesResult.reason, { path: 'after-move', workspaceId, transactionId });
        }
        if (emailFailed) {
            reportRelocateFailure(emailResult.reason, { path: 'after-move-email', workspaceId, transactionId });
        }
        if (filesFailed && emailFailed) showToast(MOVED_BOTH_PENDING_MESSAGE, 'info');
        else if (emailFailed) showToast(MOVED_EMAIL_PENDING_MESSAGE, 'info');
        else if (filesFailed) showToast(MOVED_FILES_PENDING_MESSAGE, 'info');
    };

    // A deal that was itself moved and never finished relocating its email must
    // finish before it moves again, or its email would be stranded two moves
    // back. Best effort: a failure is reported and never stops the move.
    const finishEarlierEmailMove = async (transaction, currentWorkspaceId) => {
        const from = transaction?.movedFrom;
        if (!from || typeof from.workspaceId !== 'string' || typeof from.transactionId !== 'string') return;
        if (transaction.emailRelocatedAt || !currentWorkspaceId || !transaction.id) return;
        try {
            await transactionService.relocateMovedDealEmail(currentWorkspaceId, transaction.id);
        } catch (error) {
            reportRelocateFailure(error, {
                path: 'before-move-email',
                workspaceId: currentWorkspaceId,
                transactionId: transaction.id,
            });
        }
    };

    const handleCreateMemberInvitation = async (email) => {
        if (!selectedWorkspaceId) return;
        if (!canManageWorkspaceAccess) {
            showToast(MANAGE_ACCESS_OWNER_ONLY_MESSAGE, 'error');
            return;
        }
        await run(async () => {
            await invitationService.createInvitation({
                teamWorkspaceId: selectedWorkspaceId,
                email,
                role: 'member',
            });
            showToast(`Invitation sent to ${email}.`, 'success');
        });
    };

    const handleCreateTeamAgentInvitation = async (email, tawTierId) => {
        if (!selectedWorkspaceId) return;
        if (!canManageWorkspaceAccess) {
            showToast(MANAGE_ACCESS_OWNER_ONLY_MESSAGE, 'error');
            return;
        }
        await run(async () => {
            await invitationService.createInvitation({
                teamWorkspaceId: selectedWorkspaceId,
                email,
                role: 'team_agent',
                tawTierId,
            });
            showToast(`Team Agent invitation sent to ${email}.`, 'success');
        });
    };

    const handleResendInvitation = async (token) => {
        await run(async () => {
            await invitationService.resendInvitation(token);
            showToast('Invitation re-sent.', 'success');
        });
    };

    const handleRevokeInvitation = async (token) => {
        await run(async () => {
            await invitationService.revokeInvitation(token);
            showToast('Invitation revoked.', 'info');
        });
    };

    const handleRemoveWorkspaceMember = async (memberId) => {
        if (!selectedWorkspaceId || !selectedWorkspace || !canManageWorkspaceAccess) {
            showToast(MANAGE_ACCESS_OWNER_ONLY_MESSAGE, 'error');
            return;
        }
        const normalizedMemberId = (memberId || '').trim();
        if (!normalizedMemberId) return;
        const ownerId = (selectedWorkspace.ownerId || '').trim();
        if (normalizedMemberId === ownerId) {
            showToast("You cannot remove the team owner.", "error");
            return;
        }
        if (!window.confirm("Remove this user from the team?")) return;
        await run(async () => {
            await invitationService.removeWorkspaceSeat({
                teamWorkspaceId: selectedWorkspaceId,
                uid: normalizedMemberId,
                role: 'member',
            });
            setWorkspaceMembers((current = []) => current.filter(member => member.uid !== normalizedMemberId));
            showToast("Team access removed.");
        }, "Failed to remove selected user.");
    };

    const handleCreateWorkspace = async (name) => {
        if (!user || !name.trim()) return;
        const defaults = sanitizeWorkspaceDataEntrySettings(DEFAULT_WORKSPACE_DATA_ENTRY_SETTINGS);
        const result = await run(async () => {
            // Ensure the caller has a userAccount doc (idempotent — no-op if it
            // already exists). Required precondition for createTeamWorkspace.
            await invitationService.ensureUserAccount();
            const { teamWorkspaceId } = await invitationService.createTeamWorkspace({
                name: name.trim(),
            });
            // The server-side callable does not seed dataEntryDefaults — apply
            // it as an owner update post-creation. Plan 5 update rule permits
            // this (dataEntryDefaults is not a restricted field).
            await workspaceService.updateWorkspaceSettings(teamWorkspaceId, {
                dataEntryDefaults: defaults,
            });
            setSelectedWorkspaceId(teamWorkspaceId);
            setCreateWorkspaceModalOpen(false);
            showToast("Team created!");
            return teamWorkspaceId;
        }, "Couldn't create the team. Try again.");
        return result ?? null;
    };

    // The caller (RenameTeamModal) owns the success toast, close, and error
    // toast — this stays a pure action that throws so the modal can react.
    const handleRenameWorkspace = async (name) => {
        if (!selectedWorkspaceId || !name.trim()) return;
        await workspaceService.renameWorkspace(selectedWorkspaceId, name.trim());
    };

    const handleDeleteWorkspace = async () => {
        if (!selectedWorkspaceId) return;
        // Every failure is rethrown so the open modal reacts (review IN-03): a
        // failed-precondition refusal (live subscription, child team agent
        // workspaces; D-20 and D-21) keeps its details so the modal swaps to
        // the matching state, and any other error shows the modal's own
        // "Couldn't delete the team" alert instead of a toast behind it.
        let failure = null;
        await run(async () => {
            try {
                await workspaceService.deleteWorkspaceViaCallable(selectedWorkspaceId);
            } catch (error) {
                failure = error;
                return;
            }
            setDeleteWorkspaceModalOpen(false);
            showToast("Team deleted. Its data is being removed.");
        }, "Couldn't delete the team. Nothing was removed. Try again.");
        if (failure) throw failure;
    };

    const handleMoveTransaction = async (targetWorkspaceId) => {
        if (!transactionToMove || !targetWorkspaceId || !selectedWorkspaceId) return;
        const transaction = transactionToMove;
        let moved = null;
        await finishEarlierEmailMove(transaction, selectedWorkspaceId);
        await run(async () => {
            moved = await transactionService.moveTransaction(selectedWorkspaceId, targetWorkspaceId, transaction);
            setMoveTransactionModalOpen(false);
            setTransactionToMove(null);
            showToast("Transaction moved successfully!");
        }, "Failed to move transaction.");
        await relocateAfterMove(transaction, targetWorkspaceId, moved);
    };

    // T-41-STALE-ROSTER: each handler below returns a truthy value ONLY when
    // its write actually succeeded, so ManageWorkspacePage.js can gate its local
    // useTaws() patchTaw() call on real success. This is load-bearing: run()
    // (useAsyncHandler.js) catches every error internally, toasts it, and
    // resolves undefined -- it never rethrows -- so before this change a
    // caller had NO way to distinguish "saved" from "failed" by awaiting
    // these functions. Patching the roster unconditionally on that
    // indistinguishable resolution would have shown a value that was never
    // actually written on a failed save. Returning `true` from inside the
    // run() callback only reaches the caller on the success path; the catch
    // block has no return, so a failure still resolves undefined (falsy).
    const handleUpdateChildPolicy = async (tawId, resource, patch) => {
        if (!tawId || !resource) return false;
        return run(async () => {
            await workspaceService.updateChildInheritancePolicy(tawId, resource, patch);
            return true;
        }, 'Failed to update policy.');
    };

    // T-41-SIBLING-TOASTS (gap-fix, follow-up to T-41-DOUBLE-TOAST): the same
    // double-toast / false-success-on-failure shape found and fixed for
    // rename also existed here -- this handler toasted on success AND
    // TawsTab.js's handleSplitSave ALSO toasted after awaiting it, inside a
    // try/catch that run() (useAsyncHandler.js) could never actually trigger
    // (it swallows every error and never rethrows). The caller already owns
    // the fuller split-editing UI lifecycle (dirty-state reset, input
    // strip), so it stays the single owner of the success toast; this
    // handler stays a pure write-then-signal action, matching rename.
    const handleUpdateChildSplit = async (tawId, percent) => {
        if (!tawId) return false;
        const clamped = Math.min(100, Math.max(0, Number(percent) || 0));
        return run(async () => {
            await workspaceService.updateChildSplit(tawId, clamped);
            return true;
        }, 'Failed to update split.');
    };

    // T-41-SIBLING-TOASTS (gap-fix): same shape as split/rename above --
    // this used to toast 'Removing agent — transferring their work…' here
    // AND TawsTab.js's handleDeleteTaw ALSO toasted `${taw.name} deleted`
    // after awaiting it (two toasts with genuinely different text for one
    // action), inside a try/catch run() could never trigger. This also
    // never returned a success/failure signal at all (the caller had no way
    // to know), unlike every other TAW mutation handler in this file. Now
    // returns true only past a confirmed write; the caller (which owns
    // closing the delete modal via onSuccess) owns the single success toast.
    const handleRemoveTaw = async (tawId, childName, childOwnerUid, onSuccess) => {
        if (!selectedWorkspaceId || !tawId || !childOwnerUid) return false;
        return run(async () => {
            await invitationService.removeTeamAgent({
                teamWorkspaceId: selectedWorkspaceId,
                uid: childOwnerUid,
            });
            if (typeof onSuccess === 'function') onSuccess();
            return true;
        }, 'Failed to delete child workspace.');
    };

    const handleOpenTaw = (tawId) => {
        if (!tawId) return;
        setSelectedWorkspaceId(tawId);
    };

    // T-41-DOUBLE-TOAST (gap-fix, found during live dev verification): this
    // used to call showToast('Workspace renamed.') here AND TawsTab.js's
    // handleRenameConfirm called its own showToast('Workspace renamed') a
    // moment later -- one user action produced two success toasts (reported
    // live: "I got 2 green toasts that the change was successful"). The
    // caller (TawsTab.js) already owns the full rename UI lifecycle --
    // closing rename mode, refocusing, and now gating its own toast on the
    // real `ok` return value below -- so this handler stays a pure
    // write-then-signal action; the single success confirmation belongs to
    // the caller that has that context. The failure toast stays here (run()
    // is the only layer that ever sees the rejected write).
    const handleRenameChild = async (tawId, name) => {
        if (!tawId || !name.trim()) return false;
        return run(async () => {
            await workspaceService.renameWorkspace(tawId, name.trim());
            return true;
        }, 'Failed to rename workspace.');
    };

    // T-41-SIBLING-TOASTS (gap-fix): same shape as split/rename above --
    // this used to toast 'Member removed.' here AND TawsTab.js's
    // handleRemoveMemberConfirm ALSO toasted `${name} removed` after
    // awaiting it, inside a try/catch run() could never trigger. The
    // caller already owns closing the confirm modal, so it stays the
    // single owner of the success toast (and can name the removed member,
    // which this generic handler-layer message could not).
    const handleRemoveChildMember = async (tawId, memberId, taw = null) => {
        if (!tawId || !memberId) return false;
        const memberDirectory = taw?.memberDirectory || {};
        const memberEntry = memberDirectory[memberId];
        if (memberEntry?.teamRole === 'parent-owner') {
            showToast('Cannot remove the parent team owner.', 'error');
            return false;
        }
        if (!window.confirm('Remove this user from the child workspace?')) return false;
        return run(async () => {
            await invitationService.removeWorkspaceSeat({
                teamWorkspaceId: tawId,
                uid: memberId,
                role: 'member',
            });
            // teamMemberIds is a legacy denormalized mirror on the parent workspace
            // (parent.teamMemberIds tracks UIDs that are members of any child workspace).
            // removeWorkspaceSeat does not touch parent docs, so we keep this client-side
            // write to preserve behavioral parity. teamMemberIds is NOT in the Plan 5
            // restricted-field list, so this write remains legal under the new rules.
            await workspaceService.removeTeamMemberFromParent(selectedWorkspaceId, memberId);
            return true;
        }, 'Failed to remove member.');
    };

    const handleAssignTransactionToChild = async (transaction, tawId) => {
        if (!selectedWorkspaceId || !transaction?.id || !tawId) return;
        let moved = null;
        await finishEarlierEmailMove(transaction, selectedWorkspaceId);
        await run(async () => {
            moved = await workspaceService.assignTransactionToChild(selectedWorkspaceId, tawId, transaction);
            const targetName = getWorkspaceName(tawId);
            showToast(targetName ? `Deal assigned to ${targetName}.` : 'Deal assigned to the agent workspace.');
        }, 'Failed to assign transaction.');
        await relocateAfterMove(transaction, tawId, moved);
    };

    // Generic reassign — handles all direction combos:
    //   parent → child  (uses assignTransactionToChild for contact copy)
    //   child → parent  (simple move; contacts remain in child)
    //   child → child   (simple move)
    const handleReassignTransaction = async (transaction, targetWorkspaceId) => {
        if (!selectedWorkspaceId || !transaction?.id || !targetWorkspaceId) return;
        const sourceWorkspaceId = transaction._workspaceId || selectedWorkspaceId;
        if (sourceWorkspaceId === targetWorkspaceId) return;

        let moved = null;
        await finishEarlierEmailMove(transaction, sourceWorkspaceId);
        await run(async () => {
            const isFromChild = !!transaction._workspaceId;
            const isToChild = targetWorkspaceId !== selectedWorkspaceId;

            // Clean internal enrichment tags before moving the document
            const { _workspaceId, _workspaceName, _splitPercentToAgent, ...cleanTx } = transaction;

            if (isFromChild && isSoldStatus(cleanTx.status)) {
                try { await workspaceService.deleteTwTeamSplitIncome(selectedWorkspaceId, cleanTx.id); } catch (_) { /* best-effort cleanup; ignore failures */ }
            }

            if (!isFromChild && isToChild) {
                // Parent → Child: full assignment (copies contacts into child)
                moved = await workspaceService.assignTransactionToChild(selectedWorkspaceId, targetWorkspaceId, cleanTx);
            } else {
                // Child → Parent or Child → Other Child: simple document move
                moved = await transactionService.moveTransaction(sourceWorkspaceId, targetWorkspaceId, cleanTx);
            }

            if (isToChild) {
                const targetName = getWorkspaceName(targetWorkspaceId);
                showToast(targetName ? `Deal moved to ${targetName}.` : 'Deal moved.');
            } else {
                showToast('Transaction moved to your workspace.');
            }
        }, 'Failed to reassign transaction.');
        await relocateAfterMove(transaction, targetWorkspaceId, moved);
    };

    return {
        handleCreateMemberInvitation,
        handleCreateTeamAgentInvitation,
        handleResendInvitation,
        handleRevokeInvitation,
        handleRemoveWorkspaceMember,
        handleCreateWorkspace,
        handleRenameWorkspace,
        handleDeleteWorkspace,
        handleMoveTransaction,
        handleUpdateChildPolicy,
        handleUpdateChildSplit,
        handleRemoveTaw,
        handleOpenTaw,
        handleRenameChild,
        handleRemoveChildMember,
        handleAssignTransactionToChild,
        handleReassignTransaction,
    };
}
